import { useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Volume2, Bookmark, BookmarkCheck, Type, MessageCircle, HelpCircle, BookOpen, Loader2, Trash2, Image, SpellCheck } from 'lucide-react';
import { domToPng } from 'modern-screenshot';
import { addXP } from '../store';
import { triggerXPAnimation } from './XPAnimationPortal';
import { playTTS } from '../utils/tts';
import './LearningCard.css';

const TYPE_ICONS = {
  'Term': BookOpen,
  'Sentence': MessageCircle,
  'Grammar': Type,
  'Culture': BookOpen,
  'Correction': SpellCheck,
  'Question': HelpCircle,
  // Legacy types (for backward compatibility)
  'Vocabulary': BookOpen,
  'Phrase': BookOpen,
  'Adverb': Type,
  'Adjective': Type,
};

/**
 * Parse mellow:card content format:
 * type: vocabulary
 * title: Word
 * translation: 单词 (optional, for vocabulary cards)
 * pronunciation: /wɜːrd/ (optional, for vocabulary cards)
 * ---
 * Body content (markdown)
 */
function parseCardContent(content) {
  const lines = content.split('\n');
  let type = 'Expression';
  let title = '';
  let translation = '';
  let pronunciation = '';
  let bodyStart = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (line.startsWith('type:')) {
      type = line.substring(5).trim();
      // Capitalize first letter
      type = type.charAt(0).toUpperCase() + type.slice(1);
    } else if (line.startsWith('title:')) {
      title = line.substring(6).trim();
    } else if (line.startsWith('translation:')) {
      translation = line.substring(12).trim();
    } else if (line.startsWith('pronunciation:')) {
      pronunciation = line.substring(14).trim();
    } else if (line === '---') {
      bodyStart = i + 1;
      break;
    }
  }

  const body = lines.slice(bodyStart).join('\n').trim();
  return { type, title, translation, pronunciation, body };
}

// Export parseCardContent for use in other components
export { parseCardContent };

export default function LearningCard({
  type,
  title,
  meaning,
  content, // Raw mellow:card content string
  color = 'var(--color-primary)',
  isSaved = false,
  onSave,
  // Compact mode for card list
  compact = false,
  showDelete = false,
  onDelete,
  dueText,
  stateText,
  languageCode, // Language code for XP tracking
  disabled = false, // When true, hide save/delete buttons (for shared view)
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const saveButtonRef = useRef(null);
  const cardRef = useRef(null);

  // If content prop is provided, parse it
  let cardType = type;
  let cardTitle = title;
  let cardBody = meaning;
  let cardTranslation = '';
  let cardPronunciation = '';

  if (content) {
    const parsed = parseCardContent(content);
    cardType = parsed.type;
    cardTitle = parsed.title;
    cardTranslation = parsed.translation;
    cardPronunciation = parsed.pronunciation;
    cardBody = parsed.body;
  }

  // Check if this card type should show translation + pronunciation inline
  // Includes: term, sentence, correction, and legacy vocabulary/phrase types
  const hasTranslationMeta = ['term', 'vocabulary', 'phrase', 'sentence', 'correction'].includes(cardType?.toLowerCase());

  const Icon = TYPE_ICONS[cardType] || Type;

  // Handle TTS playback
  const handlePlay = async (e) => {
    e?.stopPropagation();
    if (isPlaying || !cardTitle) return;

    setIsPlaying(true);
    try {
      await playTTS(cardTitle, languageCode);
    } catch (err) {
      console.error('TTS error:', err);
    } finally {
      setIsPlaying(false);
    }
  };

  // Handle save with card data
  const handleSave = (e) => {
    e?.stopPropagation();

    // Show XP animation at button position using portal
    if (saveButtonRef.current) {
      const rect = saveButtonRef.current.getBoundingClientRect();
      const xpAmount = isSaved ? -1 : 1;
      triggerXPAnimation(xpAmount, {
        x: rect.left + rect.width / 2, // Center horizontally
        y: rect.top, // Top of button (tooltip will appear above)
      });
      // Add XP with language tracking
      addXP(xpAmount, 'save', languageCode);
    }

    if (onSave) {
      onSave({ front: cardTitle, back: cardBody, cardType, translation: cardTranslation, pronunciation: cardPronunciation });
    }
  };

  // Handle delete
  const handleDelete = (e) => {
    e?.stopPropagation();
    if (onDelete) {
      onDelete();
    }
  };

  // Handle export to image
  const handleExport = async (e) => {
    e?.stopPropagation();
    if (!cardRef.current || isExporting) return;

    try {
      setIsExporting(true);

      // Generate PNG with 2x scale for high quality
      const dataUrl = await domToPng(cardRef.current, {
        quality: 1,
        scale: 2,
        backgroundColor: '#ffffff',
        style: {
          paddingLeft: '5px',
        },
      });

      // Download the image
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.download = `mellow-card-${timestamp}.png`;
      link.href = dataUrl;
      link.click();

      setTimeout(() => setIsExporting(false), 1000);
    } catch (err) {
      console.error('Failed to export card:', err);
      setIsExporting(false);
    }
  };

  return (
    <div className={`learning-card-container ${compact ? 'compact' : ''}`} ref={cardRef}>
      <div className="learning-card-body">
        <div className="learning-card-header">
          <div className="card-type-wrapper" style={{ color: color }}>
             <Icon size={14} strokeWidth={2.5} />
             <span className="card-type-label">{cardType}</span>
          </div>
          <div className="card-actions">
            {/* Due/state text in header for compact mode */}
            {compact && (dueText || stateText) && (
              <div className="card-meta-inline">
                {stateText && <span className="card-state">{stateText}</span>}
                {dueText && <span className="card-due">{dueText}</span>}
              </div>
            )}
            {!compact && !disabled && (
              <>
                <button
                  className="icon-action-btn card-export-btn"
                  onClick={handleExport}
                  disabled={isExporting}
                  title="Export as image"
                >
                  {isExporting ? <Loader2 size={18} className="spin" /> : <Image size={18} />}
                </button>
                <button
                  className={`icon-action-btn ${isPlaying ? 'playing' : ''}`}
                  onClick={handlePlay}
                  disabled={isPlaying}
                  title="Read aloud"
                >
                  {isPlaying ? <Loader2 size={18} className="spin" /> : <Volume2 size={18} />}
                </button>
              </>
            )}
            {onSave && !disabled && (
              <button
                ref={saveButtonRef}
                className="icon-action-btn"
                onClick={handleSave}
                style={{ color: isSaved ? 'var(--color-accent-orange)' : undefined }}
              >
                {isSaved ? <BookmarkCheck size={18} /> : <Bookmark size={18} />}
              </button>
            )}
            {showDelete && onDelete && !disabled && (
              <button className="icon-action-btn delete-btn" onClick={handleDelete} title="Remove from review">
                <Trash2 size={18} />
              </button>
            )}
          </div>
        </div>

        <div className="learning-card-content">
          <h3 className="card-title">
            {cardTitle}
            {hasTranslationMeta && cardTranslation && (
              <span className="card-title-meta"> · {cardTranslation}</span>
            )}
            {hasTranslationMeta && cardPronunciation && (
              <span className="card-title-pronunciation"> · {cardPronunciation}</span>
            )}
          </h3>
          {!compact && <div className="card-divider" />}
          <div className="card-meaning">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {/* {compact ? (cardBody.length > 80 ? cardBody.slice(0, 80) + '...' : cardBody) : cardBody} */}
              {cardBody}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  );
}
